/**
 * useCheckin Hook + CheckinProvider Context
 * CCH Care — Facility check-in (per-staff presence)
 *
 * Path: src/hooks/useCheckin.js
 *
 * Shared app-wide check-in state, modeled on ShiftProvider:
 * - Self-gates on authentication AND on role — only the float/oversight
 *   cohort (Nurse, Care Supervisor, Care Admin, Physician) checks in. CNAs
 *   are rostered via shift_assignments and don't need to, so no fetch fires
 *   for them.
 * - Clears state on logout so presence doesn't leak across sessions.
 * - Mount once near the app root, inside AuthProvider (alongside
 *   ShiftProvider). See App.jsx:
 *     <AuthProvider>
 *       <ShiftProvider>
 *         <CheckinProvider>
 *           <CareRouter />
 *         </CheckinProvider>
 *       </ShiftProvider>
 *     </AuthProvider>
 *
 * Consumes /api/shifts/checkin.php via src/api/checkin.js. The first
 * consumer of this presence signal is MAR witness / controlled co-sign
 * eligibility (mar.php?action=witnesses reads care_facility_checkins).
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useAuth } from '../features/auth/useAuth';
import { getCheckinStatus, checkIn as apiCheckIn, checkOut as apiCheckOut } from '../api/checkin';

// The cohort that lacks shift_assignments and therefore needs to declare
// presence explicitly. Keep in sync with LICENSED_ROLES elsewhere.
const CHECKIN_ROLES = ['Nurse', 'Care Supervisor', 'Care Admin', 'Physician'];

const CheckinContext = createContext(null);

export function CheckinProvider({ children }) {
  const { user, memberships } = useAuth();
  const isAuthenticated = !!user;
  const role            = memberships?.[0]?.role ?? '';
  const usesCheckin     = CHECKIN_ROLES.includes(role);

  const [checkedIn, setCheckedIn]   = useState(null);  // { facility_id, facility_name, checked_in_at, ... } | null
  const [facilities, setFacilities] = useState([]);    // [{ facility_id, name }]
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !usesCheckin) { setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const data = await getCheckinStatus();
      if (data.success) {
        setCheckedIn(data.checked_in || null);
        setFacilities(data.facilities || []);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load check-in status');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, usesCheckin]);

  useEffect(() => { refresh(); }, [refresh]);

  // Clear on logout so presence doesn't leak into a later session.
  useEffect(() => {
    if (!isAuthenticated) {
      setCheckedIn(null);
      setFacilities([]);
      setError(null);
    }
  }, [isAuthenticated]);

  const checkIn = useCallback(async (facilityId) => {
    setError(null);
    const data = await apiCheckIn(facilityId);
    if (data.success) setCheckedIn(data.checked_in || null);
    else setError(data.error || 'Check-in failed');
    return data;
  }, []);

  const checkOut = useCallback(async () => {
    setError(null);
    const data = await apiCheckOut();
    if (data.success) setCheckedIn(null);
    else setError(data.error || 'Check-out failed');
    return data;
  }, []);

  const value = {
    checkedIn,
    facilities,
    usesCheckin,
    loading,
    error,
    checkIn,
    checkOut,
    refresh,
  };

  return <CheckinContext.Provider value={value}>{children}</CheckinContext.Provider>;
}

/**
 * useCheckin — read shared check-in state from context.
 * Throws if used outside CheckinProvider so misplaced consumers fail loud.
 */
export default function useCheckin() {
  const ctx = useContext(CheckinContext);
  if (!ctx) {
    throw new Error('useCheckin must be used within a CheckinProvider');
  }
  return ctx;
}